import { useAppNavigate } from "hooks/useAppNavigate";
import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import InfoBanner from "components/InfoBanner";
import { changePasswordWithPasswordId, forgotPassword } from "services/auth.service";
import { useLoginPage } from "hooks/useLoginPage";
import { isValidEmail, isValidPassword } from "utils/validators";
import TextInput from "components/TextInput";
import PasswordInput from "components/PasswordInput";
import { useText } from "context/TextProvider";
import { getFlowState, setFlowState, clearFlowState } from "../../storage";

function ForgotPassword(): JSX.Element {
  const text = useText();
  const state = useLocation().state as { email?: string } | null;
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  const flowState = getFlowState(configId);
  const [step, setStep] = useState<1 | 2>((flowState.step as 1 | 2) || 1);
  const [email, setEmail] = useState(state?.email || flowState.email || "");
  const [verificationId, setVerificationId] = useState(flowState.verificationId || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Leaving this flow before completing it (back button, navigating away)
  // must not leave a stale step/verificationId behind — otherwise
  // revisiting "Forgot Password?" resumes on the reset-password step
  // instead of starting over at the send-code step.
  useEffect(() => {
    return () => clearFlowState(configId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!email) {
      setError(text.forgotPassword.emailRequired);
      return;
    }
    if (!isValidEmail(email)) {
      setError(text.forgotPassword.invalidEmail);
      return;
    }
    setLoading(true);
    try {
      await forgotPassword(email, loginPageConfig?.urls?.start_forgot_password);
      setMessage(
        `${text.forgotPassword.codeSentPrefix} ${email}.
${text.forgotPassword.checkSpamFolder}`,
      );
      setStep(2);
      setFlowState(configId, { step: 2, email });
    } catch {
      setError(text.forgotPassword.sendOtpFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!verificationId) {
      setError(text.forgotPassword.verificationIdRequired);
      return;
    }
    if (!newPassword) {
      setError(text.forgotPassword.newPasswordRequired);
      return;
    }
    if (!isValidPassword(newPassword)) {
      setError(text.forgotPassword.passwordRequirements);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(text.forgotPassword.passwordsDoNotMatch);
      return;
    }
    setLoading(true);
    try {
      await changePasswordWithPasswordId({
        passwordId: verificationId,
        password: newPassword,
        loginId: email,
        url: loginPageConfig?.urls?.submit_forgot_password_otp,
      });
      setEmail("");
      setVerificationId("");
      setNewPassword("");
      setConfirmPassword("");
      clearFlowState(configId);
      appNavigate(`/password-reset-success`);
    } catch {
      setError(text.forgotPassword.resetFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta name="Forgot Password" />
      <PageTitle>{text.forgotPassword.title}</PageTitle>

      {message && <InfoBanner>{message}</InfoBanner>}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {step === 1 && (
        <form onSubmit={handleSendEmail}>
          <TextInput
            id="email"
            label={text.forgotPassword.emailLabel}
            type="email"
            value={email}
            placeholder={text.forgotPassword.emailPlaceholder}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            required
          />
          <LoadingButton
            loading={loading}
            type="submit"
            disabled={loading}
            sx={{ width: "100%", height: "3.5rem", mb: "1rem" }}
          >
            {text.forgotPassword.sendOtpButton}
          </LoadingButton>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleResetPassword}>
          <TextInput
            id="verificationId"
            label={text.forgotPassword.otpLabel}
            type="text"
            value={verificationId}
            placeholder={text.forgotPassword.otpPlaceholder}
            onChange={(e) => { setVerificationId(e.target.value); setFlowState(configId, { verificationId: e.target.value }); }}
            disabled={loading}
            required
          />
          <PasswordInput
            id="newPassword"
            label={text.forgotPassword.newPasswordLabel}
            value={newPassword}
            placeholder={text.forgotPassword.newPasswordPlaceholder}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
          />
          {newPassword && !isValidPassword(newPassword) && <ErrorMessage>{text.forgotPassword.passwordRequirements}</ErrorMessage>}
          <PasswordInput
            id="confirmPassword"
            label={text.forgotPassword.confirmPasswordLabel}
            value={confirmPassword}
            placeholder={text.forgotPassword.confirmPasswordPlaceholder}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />

          <LoadingButton
            loading={loading}
            type="submit"
            disabled={loading}
            sx={{ width: "100%", height: "3.5rem", mb: "1rem" }}
          >
            {text.forgotPassword.resetPasswordButton}
          </LoadingButton>
        </form>
      )}

      <Box sx={{ textAlign: "center", fontSize: "0.875rem", color: "text.secondary", mb: "1.5rem" }}>
        <MuiLink color="secondary" component={RouterLink} to={`/login?config_id=${configId}`} replace sx={{ fontWeight: 500 }}>
          {text.forgotPassword.backToLoginLink}
        </MuiLink>
      </Box>
    </>
  );
}

export default ForgotPassword;
